import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import router from '@Site/Router'
import AuthInput from './AuthInput'
import SocialAuthButton from './SocialAuthButton'
import submitGoogleOauthForm from './SubmitGoogleOauthForm'
import Button from '@SiteComponents/Button'
import GoogleIcon from '@Images/google-icon.svg'

import {
  validateEmail,
  validateEmailOrDomain,
  handleBlurField,
  handleChangeField
} from '@Utils/AuthenticationValidationHelpers'

import {
  signIn,
  ssoSignIn,
} from '@SiteModules/Authentication/actions'

import {
  getLoading,
} from '@SiteModules/Authentication/selectors'

import {
  FormErrors,
  IIdentityBridgePayload,
} from '@SiteModules/Authentication/types'

const SignInForm: React.FC = () => {
  const dispatch = useDispatch()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(false)
  const [sso, setSso] = useState(false)
  const [emailOrDomain, setEmailOrDomain] = useState('')
  const [errors, setErrors] = useState<FormErrors>({})
  const [touched, setTouched] = useState<Record<string, boolean>>({})

  const loading = useSelector(getLoading)
  const hasErrorsForEmailLogin = Boolean(errors.email || errors.password)
  const hasErrorsForSsoLogin = Boolean(errors.emailOrDomain)
  const isDisabledBecauseOfInput = sso ? (!emailOrDomain.trim() || hasErrorsForSsoLogin) : (!email.trim() || !password.trim() || hasErrorsForEmailLogin)
  const isDisabled = loading || isDisabledBecauseOfInput

  const getIdentityBridgePayload = (): IIdentityBridgePayload => {
    const searchParams = new URLSearchParams(window.location.search)
    const anonymousId = searchParams.get('anonymous_id')
    const sessionId = searchParams.get('session_id')
    const referrerPage = searchParams.get('referrer_page')
    const originalReferrer = searchParams.get('original_referrer')

    return {
      ...(anonymousId ? { anonymous_id: anonymousId } : {}),
      ...(sessionId ? { session_id: sessionId } : {}),
      ...(referrerPage ? { referrer_page: referrerPage } : {}),
      ...(originalReferrer ? { original_referrer: originalReferrer } : {}),
    }
  }

  const handleEmailBlur = () => {
    handleBlurField('email', email, setTouched, setErrors, validateEmail)
  }

  const handleEmailOrDomainBlur = () => {
    handleBlurField('emailOrDomain', email, setTouched, setErrors, validateEmailOrDomain)
  }

  const goToForgotPassword = () => {
    router.navigate('/users/password/new')
  }

  const goToSignUp = () => {
    router.navigate('/users/sign_up')
  }

  const logIn = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (sso) {
      dispatch(ssoSignIn({ emailOrDomain }))
    } else {
      dispatch(signIn({ email, password, rememberMe: +rememberMe, ...getIdentityBridgePayload() }))
    }
  }

  const forwardToGoogle = () => {
    submitGoogleOauthForm()
  }

  const toggleSso = () => {
    setSso(!sso)
  }

  const handleEmailChange = (val: string) => {
    setEmail(val)
    handleChangeField('email', val, touched, setErrors, validateEmail)
  }

  const handleEmailOrDomainChange = (val: string) => {
    setEmailOrDomain(val)
    handleChangeField('emailOrDomain', val, touched, setErrors, validateEmailOrDomain)
  }

  const handleRememberMe = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRememberMe(e.target.checked)
  }

  const renderTitle = () => {
    return (
      <React.Fragment>
        <h1 className="FormPanel__title">Welcome back</h1>
        <p className="FormPanel__subtitle">Log in to access your workspace.</p>
      </React.Fragment>
    )
  }

  const renderOtherAuth = () => {
    return (
      <div className="AuthLayout__social-auth-button-row">
        <SocialAuthButton
          label="Log in with Google"
          icon={<GoogleIcon />}
          onClick={forwardToGoogle}
          data-testid="login-with-google-submit"
        />
        <SocialAuthButton
          label={sso ? 'Log in with Email' : 'Log in with SSO'}
          onClick={toggleSso}
          data-testid={sso ? 'login-with-email-toggle' : 'login-with-sso-toggle'}
        />
      </div>
    )
  }

  const renderDivider = () => {
    return (
      <div className="AuthLayout__divider">
        <div className="AuthLayout__divider-line" />
        <span className="AuthLayout__divider-text">
          {`or with ${sso ? 'sso' : 'email'}`}
        </span>
        <div className="AuthLayout__divider-line" />
      </div>
    )
  }

  const renderInputsEmail = () => {
    return (
      <React.Fragment>
        <AuthInput
          id="signin-email"
          label="Email"
          type="email"
          placeholder="you@company.com"
          value={email}
          autoComplete="username"
          data-testid="signin-email-input"
          onChange={handleEmailChange}
          onBlur={handleEmailBlur}
          error={errors.email}
        />
        <div>
          <button type="button" className="FormPanel__forgot-link" onClick={goToForgotPassword}>
            Forgot password?
          </button>
          <AuthInput
            id="signin-password"
            label="Password"
            placeholder="Enter your password"
            value={password}
            autoComplete="current-password"
            data-testid="signin-password-input"
            onChange={setPassword}
            isPassword
          />
        </div>
        {renderRememberMe()}
      </React.Fragment>
    )
  }

  const renderRememberMe = () => {
    return (
      <div className="AuthLayout__checkbox-wrapper">
        <label className="AuthLayout__checkbox-label">
          <input
            type="checkbox"
            className="AuthLayout__checkbox"
            checked={rememberMe}
            onChange={handleRememberMe}
          />
          <div className="AuthLayout__checkbox-checkmark-wrapper">
            <div className="AuthLayout__checkbox-checkmark"/>
          </div>
          <span className="AuthLayout__checkbox-text">
            Remember me
          </span>
        </label>
      </div>
    )
  }

  const renderInputsSso = () => {
    return (
      <AuthInput
        id="signin-sso-identifier"
        label="Email or Domain"
        placeholder="you@company.com"
        value={emailOrDomain}
        autoComplete="username"
        data-testid="signin-sso-identifier-input"
        onChange={handleEmailOrDomainChange}
        onBlur={handleEmailOrDomainBlur}
        error={errors.emailOrDomain}
      />
    )
  }

  const renderBottom = () => {
    return (
      <React.Fragment>
        <div className="FormPanel__button-area">
          <Button
            styleType="brand"
            size="large"
            caption={sso ? 'Continue with SSO' : 'Log In'}
            type="submit"
            disabled={isDisabled}
            data-testid={sso ? 'login-with-sso-submit' : 'login-with-email-submit'}
          />
        </div>
        <div className="FormPanel__footer">
          <span className="FormPanel__footer-text">Don&apos;t have an account?</span>
          <button type="button" className="FormPanel__footer-link" onClick={goToSignUp}>
            Sign up
          </button>
        </div>
      </React.Fragment>
    )
  }

  return (
    <form className="SignInForm" onSubmit={logIn} noValidate>
      {renderTitle()}
      {renderOtherAuth()}
      {renderDivider()}
      {sso ? renderInputsSso() : renderInputsEmail()}
      {renderBottom()}
    </form>
  )
}

export default SignInForm
